import React from 'react';

type Props = {
  children: React.ReactNode;
  label?: string;
};

type State = { error: Error | null };

export class AppErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Keep it loud in console for root-cause debugging (prevents "black screen" mystery).
    console.error('[ui-error-boundary]', this.props.label ?? 'app', error, info);
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleHome = () => {
    window.location.assign('/');
  };

  render() {
    if (!this.state.error) return this.props.children;

    const message = this.state.error?.message || 'Unknown error';

    return (
      <div className="min-h-screen bg-neutral-950 text-neutral-100 p-6">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-xl font-semibold">Something went wrong</h1>
          <p className="text-sm text-neutral-300 mt-2">
            The dashboard hit a UI error instead of rendering a blank screen. Reload usually fixes
            it; if it keeps happening, the error text below is the clue.
          </p>

          <div className="mt-4 flex gap-3">
            <button
              type="button"
              onClick={this.handleReload}
              className="px-3 py-2 rounded-sm bg-[var(--accent)] text-black font-medium hover:bg-[var(--accent)] transition-colors"
            >
              Reload
            </button>
            <button
              type="button"
              onClick={this.handleHome}
              className="px-3 py-2 rounded-sm border border-neutral-700 text-neutral-200 hover:bg-neutral-900 transition-colors"
            >
              Go home
            </button>
          </div>

          <details className="mt-6">
            <summary className="cursor-pointer text-sm text-neutral-400">Error details</summary>
            <pre className="mt-3 whitespace-pre-wrap rounded-sm border border-neutral-800 bg-black/40 p-3 text-xs text-neutral-200 font-mono overflow-auto">
              {message}
              {'\n'}
              {(this.state.error?.stack ?? '').trim()}
            </pre>
          </details>
        </div>
      </div>
    );
  }
}
